/**
 * QualitiesPage — CRUD cho bảng list_and_valo / qualities (Sprint 5).
 * Type Quality: quality_code (PK), description, description_on_contract, premium, vol_adj.
 */
import { useEffect, useMemo, useState } from 'react'
import {
  Box,
  Button,
  IconButton,
  Paper,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material'
import AddIcon from '@mui/icons-material/Add'
import DeleteIcon from '@mui/icons-material/Delete'
import EditIcon from '@mui/icons-material/Edit'
import type { GridColDef, GridPaginationModel } from '@mui/x-data-grid'
import { useForm } from 'react-hook-form'

import ConfirmDialog from '@/components/ConfirmDialog'
import DataTableMUI from '@/components/DataTableMUI'
import FormDialog from '@/components/FormDialog'
import PageDataState from '@/components/PageDataState'
import RoleGuard from '@/components/RoleGuard'
import { usePagePermission } from '../../hooks/usePagePermission'
import {
  useListQualitiesCrudQuery,
  useCreateQualityMutation,
  useUpdateQualityMutation,
  useDeleteQualityMutation,
} from '@/store/api/masterDataCrudApi'
import { useDebounce } from '@/hooks/useDebounce'
import { useSnackbar } from '@/hooks/useSnackbar'
import { getErrorMessage } from '@/utils/error'
import { formatNumber } from '@/utils/money'
import type { Quality } from '@/types/api'

interface QualFormValues {
  quality_code: string
  description: string
  description_on_contract: string
  premium: string
  vol_adj: string
}

const EMPTY: QualFormValues = {
  quality_code: '',
  description: '',
  description_on_contract: '',
  premium: '',
  vol_adj: '',
}

export function QualitiesPage() {
  const { canEdit } = usePagePermission()
  const snack = useSnackbar()

  const [search, setSearch] = useState('')
  const debouncedSearch = useDebounce(search, 350)
  const [pagination, setPagination] = useState<GridPaginationModel>({ page: 0, pageSize: 25 })
  const [editing, setEditing] = useState<Quality | null>(null)
  const [createOpen, setCreateOpen] = useState(false)
  const [confirmDelete, setConfirmDelete] = useState<Quality | null>(null)

  const { data, isLoading, error, isFetching } = useListQualitiesCrudQuery({
    q: debouncedSearch || undefined,
    page: pagination.page + 1,
    page_size: pagination.pageSize,
  })

  const [createQuality, { isLoading: creating }] = useCreateQualityMutation()
  const [updateQuality, { isLoading: updating }] = useUpdateQualityMutation()
  const [deleteQuality, { isLoading: deleting }] = useDeleteQualityMutation()

  const dialogOpen = createOpen || !!editing
  const isEdit = !!editing
  const dialogLoading = creating || updating

  const { register, handleSubmit, reset } = useForm<QualFormValues>({ defaultValues: EMPTY })

  useEffect(() => {
    if (!dialogOpen) return
    if (isEdit && editing) {
      reset({
        quality_code: editing.quality_code,
        description: editing.description ?? '',
        description_on_contract: editing.description_on_contract ?? '',
        premium: editing.premium != null ? String(editing.premium) : '',
        vol_adj: editing.vol_adj != null ? String(editing.vol_adj) : '',
      })
    } else {
      reset(EMPTY)
    }
  }, [dialogOpen, isEdit, editing, reset])

  const rows = useMemo(
    () => (data?.items ?? []).map((r) => ({ id: r.quality_code, ...r })),
    [data],
  )

  const columns = useMemo<GridColDef[]>(
    () => [
      { field: 'quality_code', headerName: 'Code', width: 120 },
      {
        field: 'description',
        headerName: 'Description',
        flex: 1,
        minWidth: 200,
        valueFormatter: (value: unknown) => {
          const v = value as string | null | undefined
          return typeof v === 'string' && v.length > 60 ? v.slice(0, 60) + '…' : (v ?? '')
        },
      },
      {
        field: 'description_on_contract',
        headerName: 'On Contract',
        flex: 1,
        minWidth: 180,
        valueFormatter: (value: unknown) => {
          const v = value as string | null | undefined
          return typeof v === 'string' && v.length > 60 ? v.slice(0, 60) + '…' : (v ?? '')
        },
      },
      {
        field: 'premium',
        headerName: 'Premium',
        width: 120,
        valueFormatter: (value) => formatNumber(value as number | null),
      },
      {
        field: '_actions',
        headerName: 'Actions',
        width: 120,
        sortable: false,
        filterable: false,
        renderCell: (params) => (
          <Stack direction="row" spacing={0.5}>
            <Tooltip title="Edit">
              <IconButton size="small" disabled={!canEdit} onClick={(e) => { e.stopPropagation(); setEditing(params.row as Quality) }}>
                <EditIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Delete">
              <IconButton size="small" color="error" disabled={!canEdit} onClick={(e) => { e.stopPropagation(); setConfirmDelete(params.row as Quality) }}>
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Stack>
        ),
      },
    ],
    [],
  )

  const handleCloseDialog = () => { setCreateOpen(false); setEditing(null) }

  const onSubmit = handleSubmit(async (values) => {
    const payload: Quality = {
      quality_code: values.quality_code.trim(),
      description: values.description.trim(),
      description_on_contract: values.description_on_contract || null,
      premium: values.premium !== '' ? Number(values.premium) : null,
      vol_adj: values.vol_adj !== '' ? Number(values.vol_adj) : null,
    }
    try {
      if (isEdit && editing) {
        await updateQuality({ id: editing.quality_code, body: payload }).unwrap()
        snack.success(`Updated "${editing.quality_code}"`)
      } else {
        await createQuality(payload).unwrap()
        snack.success(`Created "${payload.quality_code}"`)
      }
      handleCloseDialog()
    } catch (err) {
      snack.error(getErrorMessage(err, isEdit ? 'Update failed' : 'Create failed'))
    }
  })

  const handleConfirmDelete = async () => {
    if (!confirmDelete) return
    try {
      await deleteQuality(confirmDelete.quality_code).unwrap()
      snack.success(`Deleted "${confirmDelete.quality_code}"`)
      setConfirmDelete(null)
    } catch (err) {
      snack.error(getErrorMessage(err, 'Delete failed'))
    }
  }

  const isEmpty = !isLoading && !error && (data?.items?.length ?? 0) === 0

  return (
    <Box display="flex" flexDirection="column" gap={2}>
      <Box display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={2}>
        <Typography variant="h5" fontWeight={700}>Qualities</Typography>
        <Button variant="contained" startIcon={<AddIcon />} disabled={!canEdit} onClick={() => setCreateOpen(true)}>
          New Quality
        </Button>
      </Box>

      <Paper sx={{ p: 2 }}>
        <TextField
          label="Search"
          placeholder="Code, description..."
          size="small"
          value={search}
          onChange={(e) => { setSearch(e.target.value); setPagination((p) => ({ ...p, page: 0 })) }}
          sx={{ minWidth: 260 }}
        />
      </Paper>

      <PageDataState loading={isLoading} error={error} empty={isEmpty}>
        <Paper>
          <DataTableMUI
            rows={rows}
            columns={columns}
            loading={isFetching}
            paginationMode="server"
            paginationModel={pagination}
            onPaginationModelChange={setPagination}
            rowCount={data?.total ?? 0}
            onRowClick={(p) => setEditing(p.row as Quality)}
          />
        </Paper>
      </PageDataState>

      <FormDialog
        open={dialogOpen}
        title={isEdit ? 'Edit Quality' : 'New Quality'}
        loading={dialogLoading}
        onClose={handleCloseDialog}
        onSubmit={() => void onSubmit()}
      >
        <Box display="flex" flexDirection="column" gap={2} pt={1}>
          <TextField label="Quality Code *" size="small" fullWidth {...register('quality_code')} disabled={isEdit} />
          <TextField label="Description *" size="small" fullWidth {...register('description')} />
          <TextField label="Description on Contract" size="small" fullWidth multiline rows={2} {...register('description_on_contract')} />
          <Box display="flex" gap={2} flexWrap="wrap">
            <TextField label="Premium" size="small" type="number" {...register('premium')} sx={{ flex: 1 }} />
            <TextField label="Vol Adj" size="small" type="number" {...register('vol_adj')} sx={{ flex: 1 }} />
          </Box>
        </Box>
      </FormDialog>

      <ConfirmDialog
        open={!!confirmDelete}
        title="Delete Quality"
        message={confirmDelete ? `Are you sure you want to delete "${confirmDelete.quality_code}"?` : ''}
        destructive
        loading={deleting}
        onConfirm={() => void handleConfirmDelete()}
        onCancel={() => setConfirmDelete(null)}
      />
    </Box>
  )
}

export default QualitiesPage
